import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FilterList, TuneRounded, ClearAll } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { workOrderStatuses } from '../../constants/statusOptions';
import { CollapsiblePanel } from '../../components/layout/CollapsiblePanel';
import type { Client } from '../../types/project';
import type { WorkOrdersTabFilters } from '../../types/workOrdersTab';
import { getSectionLabelSx, getInputRootSx } from './filterStyles';

interface ProjectOption {
  ProjectKey: string;
  ProjectName: string;
  ProjectId: string;
}

interface WorkOrdersFiltersProps {
  projects: ProjectOption[];
  clients: Client[];
  filters: WorkOrdersTabFilters;
  onFilterChange: (filters: WorkOrdersTabFilters) => void;
  onClear: () => void;
  statusCounts?: Record<string, number>;
}

/** Matches the WO header's Lab select (WorkOrdersTabContent). */
const LAB_OPTIONS = ['Fredericton', 'Vancouver'];

const STATUS_DOT_COLORS: Record<string, string> = {
  Scheduled: 'statusPlanning',
  'In Progress': 'statusActive',
  Completed: 'statusCompleted',
};

export const WorkOrdersFilters = ({
  projects,
  clients,
  filters,
  onFilterChange,
  onClear,
  statusCounts,
}: WorkOrdersFiltersProps) => {
  const theme = useTheme();
  const sectionLabelSx = getSectionLabelSx(theme);
  const inputRootSx = getInputRootSx(theme);

  const handleStatusToggle = (status: string) => {
    const next = filters.statuses.includes(status)
      ? filters.statuses.filter((s) => s !== status)
      : [...filters.statuses, status];
    onFilterChange({ ...filters, statuses: next });
  };

  const getStatusLabel = (status: string): string => {
    if (statusCounts && statusCounts[status] != null) {
      return `${status} (${statusCounts[status]})`;
    }
    return status;
  };

  const activeFilterCount =
    filters.statuses.length +
    (filters.projectKey ? 1 : 0) +
    (filters.clientKey ? 1 : 0) +
    (filters.labLocation ? 1 : 0) +
    (filters.startDateFrom ? 1 : 0) +
    (filters.startDateTo ? 1 : 0);

  const expandedContent = (
    <Box sx={{ px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      {/* Project */}
      <Box>
        <Typography variant="caption" sx={sectionLabelSx}>Project</Typography>
        <Autocomplete
          size="small"
          options={projects}
          getOptionLabel={(opt) => `${opt.ProjectId} - ${opt.ProjectName}`}
          value={projects.find((p) => p.ProjectKey === filters.projectKey) ?? null}
          onChange={(_e, val) => onFilterChange({ ...filters, projectKey: val?.ProjectKey ?? null })}
          renderInput={(params) => (
            <TextField {...params} placeholder="All Projects" sx={inputRootSx} />
          )}
        />
      </Box>

      {/* Client */}
      <Box>
        <Typography variant="caption" sx={sectionLabelSx}>Client</Typography>
        <Autocomplete
          size="small"
          options={clients}
          getOptionLabel={(opt) => opt.ClientName}
          value={clients.find((c) => c.ClientKey === filters.clientKey) ?? null}
          onChange={(_e, val) => onFilterChange({ ...filters, clientKey: val?.ClientKey ?? null })}
          renderInput={(params) => (
            <TextField {...params} placeholder="All Clients" sx={inputRootSx} />
          )}
        />
      </Box>

      {/* Lab */}
      <Box>
        <Typography variant="caption" sx={sectionLabelSx}>Lab</Typography>
        <Autocomplete
          size="small"
          options={LAB_OPTIONS}
          value={filters.labLocation ?? null}
          onChange={(_e, val) => onFilterChange({ ...filters, labLocation: val ?? null })}
          renderInput={(params) => (
            <TextField {...params} placeholder="All Labs" sx={inputRootSx} />
          )}
        />
      </Box>

      {/* Status */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={sectionLabelSx}>Status</Typography>
          <Typography
            variant="caption"
            onClick={() => {
              const allChecked = filters.statuses.length === workOrderStatuses.length;
              onFilterChange({ ...filters, statuses: allChecked ? [] : [...workOrderStatuses] });
            }}
            sx={{ cursor: 'pointer', color: theme.palette.primary.main, fontSize: '0.68rem', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
          >
            {filters.statuses.length === workOrderStatuses.length ? 'None' : 'All'}
          </Typography>
        </Box>
        <FormGroup>
          {workOrderStatuses.map((status) => {
            const dotColor =
              (theme.customColors as Record<string, string>)[STATUS_DOT_COLORS[status]] ?? '#9E9E9E';
            const isChecked = filters.statuses.includes(status);
            return (
              <FormControlLabel
                key={status}
                control={
                  <Checkbox
                    size="small"
                    checked={isChecked}
                    onChange={() => handleStatusToggle(status)}
                    sx={{
                      p: 0.5,
                      transition: 'all 0.15s ease',
                      '&.Mui-checked': { color: theme.palette.primary.main },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, opacity: isChecked ? 1 : 0.7 }}>
                    <Box
                      sx={{
                        width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0,
                        boxShadow: isChecked ? `0 0 0 2px ${alpha(dotColor, 0.25)}` : 'none',
                      }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: isChecked ? 500 : 400 }}>
                      {getStatusLabel(status)}
                    </Typography>
                  </Box>
                }
                sx={{
                  ml: 0, mr: 0, mb: 0.125, borderRadius: '6px', px: 0.25,
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                }}
              />
            );
          })}
        </FormGroup>
      </Box>

      {/* Date Range */}
      <Box>
        <Typography variant="caption" sx={sectionLabelSx}>Start Date Range</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            size="small" type="date" label="From" value={filters.startDateFrom}
            onChange={(e) => onFilterChange({ ...filters, startDateFrom: e.target.value })}
            InputLabelProps={{ shrink: true }} fullWidth sx={inputRootSx}
          />
          <TextField
            size="small" type="date" label="To" value={filters.startDateTo}
            onChange={(e) => onFilterChange({ ...filters, startDateTo: e.target.value })}
            InputLabelProps={{ shrink: true }} fullWidth sx={inputRootSx}
          />
        </Box>
      </Box>

      {/* Clear */}
      <Box
        component="button"
        onClick={onClear}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, background: 'none', border: 'none',
          cursor: 'pointer', color: theme.palette.text.secondary, fontSize: '0.75rem',
          fontWeight: 500, fontFamily: 'inherit', p: 0.5, borderRadius: '6px', alignSelf: 'flex-start',
          '&:hover': { color: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.06) },
        }}
      >
        <ClearAll sx={{ fontSize: 14 }} />
        Clear Filters
      </Box>
    </Box>
  );

  const collapsedContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.5, gap: 0.75 }}>
      <Tooltip title="Filters" placement="right" arrow>
        <IconButton
          size="small"
          sx={{
            width: 34, height: 34, borderRadius: '8px',
            color: activeFilterCount > 0 ? theme.palette.primary.main : theme.palette.text.secondary,
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
          }}
        >
          <TuneRounded sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      {activeFilterCount > 0 && (
        <Box
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main,
            borderRadius: '10px', px: 0.625, py: 0.125, fontSize: '0.6rem', fontWeight: 700,
            lineHeight: 1.4, minWidth: 16, textAlign: 'center',
          }}
        >
          {activeFilterCount}
        </Box>
      )}
    </Box>
  );

  return (
    <CollapsiblePanel
      title="Filters"
      titleIcon={<FilterList sx={{ fontSize: 16, color: alpha(theme.palette.text.primary, 0.45) }} />}
      expandedWidth={224}
      collapsedContent={collapsedContent}
      alignSelf="stretch"
    >
      {expandedContent}
    </CollapsiblePanel>
  );
};
